import { useState } from "react";
import { Link, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, EmptyState, IconButton, useToast } from "@agentsaas/ui";
import { pickLocalized, type Agent, type AgentCapabilityOverrides, type CapabilityRef } from "@agentsaas/protocol";
import type { CallOutput } from "@agentsaas/client-sdk";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { Loading } from "../layout/Loading";
import { NotFound } from "../pages/NotFound";
import { CollabFrame } from "../project/ProjectChrome";
import { qk, useAgents, useProject } from "../data/queries";
import { capk, refKey, useAgentCapabilities, useMcpServers } from "../data/capabilities";
import { CapIcon } from "./common";
import { CapabilityPicker, MergePreview, useMergePreview } from "./TemplateCaps";
import { applyOverrides } from "./model";

/**
 * /p/:pid/a/:aid/capabilities：agent 的"能力"页签。
 * - 当前生效（服务端解析）：能力包（来自模板 / 实例、各自版本）、skill、可用工具（平台分组 / 预授权内置工具 / 外部 MCP）、镜像、出网域名
 * - 实例级增减：在模板的能力包之上添加或移除；保存 = agentCapabilitiesPut，服务端随后重新配置 agent
 */
export function AgentCapabilitiesPage() {
  const { pid = "", aid = "" } = useParams();
  const agents = useAgents(pid);
  const project = useProject(pid);
  const agent = agents.data?.find((a) => a.id === aid);
  let body;
  if (agents.isError || (agents.data && !agent)) body = <NotFound />;
  else if (!agent || !project.data) body = <Loading />;
  else body = <AgentCapabilities key={agent.id} pid={pid} tid={project.data.tenantId} agent={agent} />;
  return (
    <CollabFrame pid={pid} section="team" mobileTab="team">
      {body}
    </CollabFrame>
  );
}

const EMPTY: AgentCapabilityOverrides = { add: [], remove: [] };
const sameOverrides = (a: AgentCapabilityOverrides, b: AgentCapabilityOverrides) => JSON.stringify(a) === JSON.stringify(b);

function AgentCapabilities({ pid, tid, agent }: { pid: string; tid: string; agent: Agent }) {
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const effective = useAgentCapabilities(agent.id);
  const template = useQuery({ queryKey: ["template", agent.templateId], queryFn: () => api.call("templateGet", { params: { tplid: agent.templateId } }) });
  const saved = agent.capabilityOverrides ?? EMPTY;
  const [draft, setDraft] = useState<AgentCapabilityOverrides>(saved);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const templateRefs: CapabilityRef[] = template.data?.capabilities ?? [];
  const refs = applyOverrides(templateRefs, draft);
  const tplBase = { mcpScopes: template.data?.mcpScopes ?? [], allowedTools: template.data?.allowedTools ?? [], image: template.data?.image ?? "" };
  const preview = useMergePreview(
    tid,
    refs.map((r) => r.ref),
    tplBase,
  );
  const resolved = preview.resolved;
  const dirty = !sameOverrides(draft, saved);
  const removedFromTemplate = templateRefs.filter((r) => draft.remove.includes(r.id));

  const removeRef = (id: string, source: "template" | "agent") =>
    setDraft((d) => (source === "agent" ? { ...d, add: d.add.filter((x) => x.id !== id) } : { ...d, remove: [...d.remove, id] }));
  const restore = (id: string) => setDraft((d) => ({ ...d, remove: d.remove.filter((x) => x !== id) }));
  const add = (id: string) =>
    setDraft((d) => (templateRefs.some((r) => r.id === id) && d.remove.includes(id) ? { ...d, remove: d.remove.filter((x) => x !== id) } : { ...d, add: [...d.add, { id, version: null, params: {} }] }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const out = await api.call("agentCapabilitiesPut", { params: { aid: agent.id }, body: draft });
      qc.setQueryData<CallOutput<"agentCapabilitiesGet">>(capk.agent(agent.id), out);
      qc.setQueryData<Agent[]>(qk.agents(pid), (xs) => xs?.map((x) => (x.id === agent.id ? { ...x, capabilityOverrides: draft } : x)));
      toast.show({ message: t("agentcap.saved", { name: agent.name }), tone: "success" });
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page" data-testid="agent-caps">
      <div className="page__head">
        <h1 className="page__title">{t("agentcap.title", { name: agent.name })}</h1>
        <Link to={`/p/${pid}/a/${agent.id}`}>{t("agentcap.back")}</Link>
      </div>

      <section className="card stack" aria-labelledby="agentcap-list">
        <div className="row">
          <h2 className="section-title grow" id="agentcap-list">
            {t("agentcap.items")}
          </h2>
          <Button size="sm" icon="plus" onClick={() => setPicking(true)} data-testid="agentcap-add">
            {t("tplcap.add")}
          </Button>
        </div>
        {template.data ? (
          <p className="muted small">
            {t("agentcap.template", { name: pickLocalized(template.data.name, locale) })}{" "}
            <Link to={`/t/${tid}/templates/${template.data.id}`}>{t("agentcap.template_open")}</Link>
          </p>
        ) : null}
        {refs.length ? (
          <ul className="cap-refs">
            {refs.map(({ ref, source }) => {
              const cap = resolved.map.get(refKey(ref));
              const name = cap ? pickLocalized(cap.manifest.name, locale) : ref.id;
              const live = effective.data?.items.find((x) => x.id === ref.id);
              return (
                <li key={ref.id} className="cap-ref" data-testid="agentcap-item" data-cap={ref.id} data-source={source}>
                  <div className="cap-ref__head">
                    <CapIcon icon={cap?.manifest.icon ?? "sparkles"} size={16} />
                    <Link to={`/t/${tid}/capabilities/${ref.id}`} className="grow ellipsis">
                      <strong>{name}</strong>
                    </Link>
                    <Badge tone={source === "agent" ? "accent" : "neutral"}>{source === "agent" ? t("agentcap.source.agent") : t("agentcap.source.template")}</Badge>
                    <Badge>{live ? t("cap.version", { version: live.version }) : ref.version ? t("tplcap.pin", { version: ref.version }) : t("agentcap.pending")}</Badge>
                    <IconButton icon="trash" label={t("tplcap.remove", { name })} onClick={() => removeRef(ref.id, source)} data-testid="agentcap-remove" />
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <EmptyState icon="sparkles" title={t("agentcap.empty")} />
        )}
        {removedFromTemplate.length ? (
          <div className="stack">
            <span className="ui-field__label">{t("agentcap.removed")}</span>
            <ul className="cap-refs">
              {removedFromTemplate.map((r) => (
                <li key={r.id} className="cap-ref" data-testid="agentcap-removed" data-cap={r.id}>
                  <div className="cap-ref__head">
                    <code className="grow">{r.id}</code>
                    <Button size="sm" variant="ghost" icon="refresh" onClick={() => restore(r.id)} data-testid="agentcap-restore">
                      {t("agentcap.restore")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {dirty ? (
          <>
            <MergePreview tid={tid} merged={preview.merged} caps={preview.resolved.map} refs={refs.map((r) => r.ref)} />
            {error ? (
              <p className="form-error" role="alert">
                {error}
              </p>
            ) : null}
            <div className="row">
              <span className="muted small grow">{t("agentcap.save_hint")}</span>
              <Button
                variant="ghost"
                onClick={() => {
                  setDraft(saved);
                  setError(null);
                }}
                data-testid="agentcap-reset"
              >
                {t("common.cancel")}
              </Button>
              <Button variant="primary" loading={busy} disabled={!!preview.merged.imageConflict} onClick={() => void save()} data-testid="agentcap-save">
                {t("agentcap.save")}
              </Button>
            </div>
          </>
        ) : null}
      </section>

      {effective.isPending ? <Loading /> : effective.data ? <Effective tid={tid} eff={effective.data} /> : null}

      <CapabilityPicker
        tid={tid}
        open={picking}
        exclude={refs.map((r) => r.ref.id)}
        onClose={() => setPicking(false)}
        onPick={(id) => {
          add(id);
          setPicking(false);
        }}
      />
    </div>
  );
}

function Effective({ tid, eff }: { tid: string; eff: CallOutput<"agentCapabilitiesGet"> }) {
  const { t } = useT();
  const servers = useMcpServers(tid);
  const none = <span className="muted">{t("cap.none")}</span>;
  return (
    <section className="card stack" aria-labelledby="agentcap-effective" data-testid="agentcap-effective">
      <h2 className="section-title" id="agentcap-effective">
        {t("agentcap.effective")}
      </h2>
      <dl className="kv">
        <dt>{t("cap.skills")}</dt>
        <dd data-testid="agentcap-skills">{eff.skills.length ? eff.skills.map((s) => <code key={s} className="cap-pill">{s}</code>) : none}</dd>
        <dt>{t("cap.runtime.image")}</dt>
        <dd data-testid="agentcap-image">
          <code>{eff.image}</code>
        </dd>
        <dt>{t("cap.runtime.egress")}</dt>
        <dd>{eff.egress.length ? eff.egress.map((s) => <code key={s} className="cap-pill">{s}</code>) : none}</dd>
      </dl>
      <h3 className="section-title">{t("agentcap.tools")}</h3>
      <div className="stack" data-testid="agentcap-tools">
        <div>
          <div className="ui-field__label">{t("agentcap.tools.platform")}</div>
          <ul className="integ-tools">
            {eff.mcpScopes.map((s) => (
              <li key={s} data-testid="agentcap-scope">
                <code>{s}</code> <span className="muted">{t(`template.scope.${s}`)}</span>
              </li>
            ))}
            {eff.mcpScopes.length ? null : <li>{none}</li>}
          </ul>
        </div>
        <div>
          <div className="ui-field__label">{t("cap.tools.allowed")}</div>
          <div>{eff.allowedTools.length ? eff.allowedTools.map((s) => <code key={s} className="cap-pill">{s}</code>) : none}</div>
        </div>
        <div>
          <div className="ui-field__label">{t("cap.tools.external")}</div>
          <ul className="integ-tools">
            {eff.externalMcp.map((x) => {
              const srv = servers.data?.find((s) => s.id === x.serverId);
              return (
                <li key={x.name} data-testid="agentcap-external">
                  <code>{x.name}</code> <span className="muted">{srv?.name ?? x.serverId}</span>
                  {srv?.tools.length ? <span className="muted small"> {srv.tools.map((tool) => `mcp__${x.name}__${tool.name}`).join(", ")}</span> : null}
                </li>
              );
            })}
            {eff.externalMcp.length ? null : <li>{none}</li>}
          </ul>
        </div>
      </div>
    </section>
  );
}
